import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Card from '../../components/Common/Card';
import Input from '../../components/Common/Input';
import Select from '../../components/Common/Select';
import MultiSelect from '../../components/Common/MultiSelect';
import Textarea from '../../components/Common/Textarea';
import Button from '../../components/Common/Button';
import api from '../../utils/api';
import { usePermission } from '../../hooks/usePermission';
import { showSuccess, showError } from '../../utils/swal';
import { ArrowLeft, Plus, X, User } from 'lucide-react';

const CustomerForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canAssign } = usePermission();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const isEditMode = !!id;
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    type: 'individual',
    status: 'new',
    customer_type: 'potential',
    budget: '',
    sales_representative_id: '',
    gender: '',
    marital_status: '',
    children_count: 0,
    birth_date: '',
    profession: '',
    education_status: '',
    nationality: 'TC',
    tax_office: '',
    tax_number: '',
    residence_ownership: '',
    source_of_registration: '',
    source_of_hearing: '',
    description: '',
    information_tags: [],
  });

  const [contacts, setContacts] = useState([
    { type: 'GSM', value: '' },
    { type: 'E-Posta', value: '' },
  ]);

  const [preferences, setPreferences] = useState({
    sms: true,
    phone: true,
    email: true,
  });

  useEffect(() => {
    fetchUsers();
    if (isEditMode) {
      fetchCustomer();
    }
  }, [id]);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data.data || response.data);
    } catch (error) {
      console.error('Kullanıcılar yüklenemedi:', error);
    }
  };

  const fetchCustomer = async () => {
    try {
      const response = await api.get(`/customers/${id}`);
      const customer = response.data;
      setFormData({
        name: customer.name || '',
        surname: customer.surname || '',
        type: customer.type || 'individual',
        status: customer.status || 'new',
        customer_type: customer.customer_type || 'potential',
        budget: customer.budget || '',
        sales_representative_id: customer.sales_representative_id || '',
        gender: customer.gender || '',
        marital_status: customer.marital_status || '',
        children_count: customer.children_count || 0,
        birth_date: customer.birth_date || '',
        profession: customer.profession || '',
        education_status: customer.education_status || '',
        nationality: customer.nationality || 'TC',
        tax_office: customer.tax_office || '',
        tax_number: customer.tax_number || '',
        residence_ownership: customer.residence_ownership || '',
        source_of_registration: customer.source_of_registration || '',
        source_of_hearing: customer.source_of_hearing || '',
        description: customer.description || '',
        information_tags: customer.information_tags || [],
      });
      if (customer.contacts && customer.contacts.length > 0) {
        setContacts(customer.contacts);
      }
      if (customer.preferences) {
        setPreferences({
          sms: customer.preferences.sms !== undefined ? customer.preferences.sms : true,
          phone: customer.preferences.phone !== undefined ? customer.preferences.phone : true,
          email: customer.preferences.email !== undefined ? customer.preferences.email : true,
        });
      }
    } catch (error) {
      console.error('Müşteri yüklenemedi:', error);
      showError('Müşteri yüklenemedi');
      navigate('/customers');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleContactChange = (index, field, value) => {
    const newContacts = [...contacts];
    newContacts[index][field] = value;
    setContacts(newContacts);
  };

  const addContact = () => {
    setContacts([...contacts, { type: 'GSM', value: '' }]);
  };

  const removeContact = (index) => {
    const newContacts = contacts.filter((_, i) => i !== index);
    setContacts(newContacts);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const filteredContacts = contacts.filter(c => c.value.trim() !== '');

      const dataToSend = {
        ...formData,
        contacts: isEditMode
          ? JSON.stringify(filteredContacts)  // Edit mode: JSON string
          : filteredContacts,                  // Create mode: array
        preferences: preferences,
      };

      console.log('Sending data:', dataToSend);
      console.log('Contacts:', dataToSend.contacts);

      if (isEditMode) {
        await api.put(`/customers/${id}`, dataToSend);
        showSuccess('Müşteri başarıyla güncellendi');
      } else {
        await api.post('/customers', dataToSend);
        showSuccess('Müşteri başarıyla eklendi');
      }
      navigate('/customers');
    } catch (error) {
      console.error('Müşteri eklenirken hata:', error);
      console.error('Error response:', error.response?.data);

      const errorMessage = error.response?.data?.message
        || JSON.stringify(error.response?.data?.errors)
        || error.message
        || 'Bilinmeyen hata';

      showError('Müşteri kaydedilirken bir hata oluştu: ' + errorMessage);
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <Button variant="secondary" size="sm" onClick={() => navigate('/customers')}>
          <ArrowLeft size={16} />
          Geri
        </Button>
      </div>

      <form onSubmit={handleSubmit}>
        <Card title="Kişisel Bilgiler" className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Ad"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Örn: Ahmet"
              required
            />
            <Input
              label="Soyad"
              name="surname"
              value={formData.surname}
              onChange={handleChange}
              placeholder="Örn: Yılmaz"
              required
            />
            <Select
              label="Müşteri Tipi"
              name="type"
              value={formData.type}
              onChange={handleChange}
              options={[
                { value: 'individual', label: 'Gerçek Kişi' },
                { value: 'corporate', label: 'Tüzel Kişi' },
              ]}
              required
            />
            <Select
              label="Cinsiyet"
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              options={[
                { value: 'male', label: 'Erkek' },
                { value: 'female', label: 'Kadın' },
                { value: 'other', label: 'Diğer' },
              ]}
            />
            <Select
              label="Medeni Durum"
              name="marital_status"
              value={formData.marital_status}
              onChange={handleChange}
              options={[
                { value: 'single', label: 'Bekar' },
                { value: 'married', label: 'Evli' },
                { value: 'divorced', label: 'Boşanmış' },
                { value: 'widowed', label: 'Dul' },
              ]}
            />
            <Input
              label="Çocuk Sayısı"
              name="children_count"
              type="number"
              value={formData.children_count}
              onChange={handleChange}
            />
            <Input
              label="Doğum Tarihi"
              name="birth_date"
              type="date"
              value={formData.birth_date}
              onChange={handleChange}
            />
            <Input
              label="Meslek"
              name="profession"
              value={formData.profession}
              onChange={handleChange}
              placeholder="Örn: Mühendis"
            />
            <Select
              label="Eğitim Durumu"
              name="education_status"
              value={formData.education_status}
              onChange={handleChange}
              options={[
                { value: 'İlkokul', label: 'İlkokul' },
                { value: 'Ortaokul', label: 'Ortaokul' },
                { value: 'Lise', label: 'Lise' },
                { value: 'Üniversite', label: 'Üniversite' },
                { value: 'Yüksek Lisans', label: 'Yüksek Lisans' },
                { value: 'Doktora', label: 'Doktora' },
              ]}
            />
            <Input
              label="Uyruk"
              name="nationality"
              value={formData.nationality}
              onChange={handleChange}
              placeholder="Örn: TC"
            />
            <Input
              label="Vergi Dairesi"
              name="tax_office"
              value={formData.tax_office}
              onChange={handleChange}
              placeholder="Örn: Kadıköy Vergi Dairesi"
            />
            <Input
              label="Vergi Numarası"
              name="tax_number"
              value={formData.tax_number}
              onChange={handleChange}
              placeholder="Örn: 1234567890"
            />
            <Select
              label="Oturduğu Konutun Mülkiyeti"
              name="residence_ownership"
              value={formData.residence_ownership}
              onChange={handleChange}
              options={[
                { value: 'Kiracı', label: 'Kiracı' },
                { value: 'Ev Sahibi', label: 'Ev Sahibi' },
                { value: 'Lojman', label: 'Lojman' },
              ]}
            />
          </div>
        </Card>

        <Card title="Müşteri Durumu" className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Müşteri Durumu"
              name="status"
              value={formData.status}
              onChange={handleChange}
              options={[
                { value: 'new', label: 'Yeni' },
                { value: 'negative', label: 'Negatif' },
                { value: 'uncertain', label: 'Belirsiz' },
                { value: 'recalled', label: 'Yeniden Aranan' },
                { value: 'unqualified', label: 'Niteliksiz' },
                { value: 'qualified', label: 'Nitelikli' },
              ]}
              required
            />
            <Select
              label="Müşteri Türü"
              name="customer_type"
              value={formData.customer_type}
              onChange={handleChange}
              options={[
                { value: 'potential', label: 'Potansiyel' },
                { value: 'former', label: 'Eski' },
                { value: 'definite_sale', label: 'Kesin Satış' },
                { value: 'definite_rental', label: 'Kesin Kiralama' },
                { value: 'option', label: 'Opsiyonlu' },
              ]}
              required
            />
            <Input
              label="Bütçe (₺)"
              name="budget"
              type="number"
              value={formData.budget}
              onChange={handleChange}
              placeholder="Örn: 5000000"
            />
            <Select
              label="Kayıt Yöntemi"
              name="source_of_registration"
              value={formData.source_of_registration}
              onChange={handleChange}
              options={[
                { value: 'Satış Ofisi', label: 'Satış Ofisi' },
                { value: 'Telefon Görüşmesi', label: 'Telefon Görüşmesi' },
                { value: 'İnternet', label: 'İnternet' },
                { value: 'Görüntülü Görüşme', label: 'Görüntülü Görüşme' },
                { value: 'E-Posta', label: 'E-Posta' },
                { value: 'WhatsApp', label: 'WhatsApp' },
              ]}
            />
            <Select
              label="Nereden Duymuş"
              name="source_of_hearing"
              value={formData.source_of_hearing}
              onChange={handleChange}
              options={[
                { value: 'Sosyal Medya', label: 'Sosyal Medya' },
                { value: 'İnternet', label: 'İnternet' },
                { value: 'Açık Hava Reklamı', label: 'Açık Hava Reklamı' },
                { value: 'Tavsiye', label: 'Tavsiye' },
                { value: 'Gazete/Dergi', label: 'Gazete/Dergi' },
                { value: 'TV/Radyo', label: 'TV/Radyo' },
              ]}
            />
            {canAssign('customers') && (
              <Select
                label="Satış Temsilcisi"
                name="sales_representative_id"
                value={formData.sales_representative_id}
                onChange={handleChange}
                options={[
                  { value: '', label: 'Seçiniz...' },
                  ...users.map(u => ({ value: u.id, label: u.name }))
                ]}
              />
            )}
          </div>
        </Card>

        <Card title="İletişim Bilgileri" className="mb-6">
          <div className="space-y-3">
            {contacts.map((contact, index) => (
              <div key={index} className="flex gap-2">
                <Select
                  label="Tip"
                  value={contact.type}
                  onChange={(e) => handleContactChange(index, 'type', e.target.value)}
                  options={[
                    { value: 'GSM', label: 'GSM' },
                    { value: 'Telefon', label: 'Telefon' },
                    { value: 'E-Posta', label: 'E-Posta' },
                    { value: 'Adres', label: 'Adres' },
                  ]}
                  className="w-1/3"
                />
                <Input
                  label="Değer"
                  value={contact.value}
                  onChange={(e) => handleContactChange(index, 'value', e.target.value)}
                  placeholder={contact.type === 'E-Posta' ? 'ornek@email.com' : '05551234567'}
                  className="flex-1"
                />
                {contacts.length > 1 && (
                  <Button
                    type="button"
                    variant="danger"
                    size="sm"
                    onClick={() => removeContact(index)}
                    className="mt-6"
                  >
                    <X size={16} />
                  </Button>
                )}
              </div>
            ))}
            <Button type="button" variant="secondary" size="sm" onClick={addContact}>
              <Plus size={16} />
              İletişim Ekle
            </Button>
          </div>
        </Card>

        <Card title="İletişim Tercihleri" className="mb-6">
          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="sms"
                checked={preferences.sms}
                onChange={(e) => setPreferences({ ...preferences, sms: e.target.checked })}
                className="w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm font-medium text-gray-700">SMS ile bilgi almak istiyorum</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="phone"
                checked={preferences.phone}
                onChange={(e) => setPreferences({ ...preferences, phone: e.target.checked })}
                className="w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm font-medium text-gray-700">Telefon ile bilgi almak istiyorum</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="email"
                checked={preferences.email}
                onChange={(e) => setPreferences({ ...preferences, email: e.target.checked })}
                className="w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm font-medium text-gray-700">E-Posta ile bilgi almak istiyorum</span>
            </label>
          </div>
        </Card>

        <Card title="Bilgi Etiketleri" className="mb-6">
          <MultiSelect
            label="Etiketler"
            name="information_tags"
            value={formData.information_tags}
            onChange={(e) => setFormData({ ...formData, information_tags: e.target.value })}
            options={[
              { value: 'Nazik', label: 'Nazik' },
              { value: 'Kaba', label: 'Kaba' },
              { value: 'Konuşkan', label: 'Konuşkan' },
              { value: 'İlgili', label: 'İlgili' },
              { value: 'Alakasız', label: 'Alakasız' },
              { value: 'Sabırlı', label: 'Sabırlı' },
              { value: 'Sabırsız', label: 'Sabırsız' },
              { value: 'Kararlı', label: 'Kararlı' },
              { value: 'Kararsız', label: 'Kararsız' },
            ]}
            placeholder="Etiket seçiniz..."
          />
        </Card>

        <Card title="Notlar" className="mb-6">
          <Textarea
            label="Açıklama"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Müşteri hakkında notlar..."
            rows={4}
          />
        </Card>

        <div className="flex gap-3 justify-end">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/customers')}
          >
            İptal
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? 'Kaydediliyor...' : isEditMode ? 'Müşteriyi Güncelle' : 'Müşteriyi Kaydet'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CustomerForm;
